import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IMsalContext } from "@azure/msal-react";
import {
  CreateTransferPayload,
  CreateTransferResult,
  TransferMode,
} from "./types";
import {
  createTransfer,
  EXTERNAL_DATA_EXCHANGE_DOMAIN,
  fetchSourceSpaces,
  fetchTransferSpaces,
  SourceSpace,
} from "./dataTransferApi";
import { extractDataTransferError } from "./extractError";
import { fetchUsers } from "components/Drawer/drawerApi";
import { IUser } from "components/Drawer/types";

const { Text } = Typography;
const { Option } = Select;

const DEBOUNCE_MS = 400;

/** Six-field AWS EventBridge cron, for example cron(0 2 * * ? *). */
const AWS_CRON_EXPRESSION = /^cron\(\s*(\S+\s+){5}\S+\s*\)$/;

/** Bucket segment is required: s3://bucket or s3://bucket/optional/path. */
const S3_URI = /^s3:\/\/[^/\s]+(\/.*)?$/i;

/** Build s3://bucket/ URI (always ends with /). */
const toS3Uri = (bucket?: string | null): string => {
  if (!bucket?.trim()) return "";
  const cleaned = bucket
    .trim()
    .replace(/^s3:\/\//i, "")
    .replace(/\/+$/, "");
  return cleaned ? `s3://${cleaned}/` : "";
};

const URI_GUIDANCE =
  "Pre-filled with the space S3 URI ending in /. You can append a path after the slash, e.g. s3://bucket-name/folder/subfolder/";

const UriLabel: React.FC<{ text: string }> = ({ text }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <Text strong>{text}</Text>
    <Tooltip title={URI_GUIDANCE}>
      <InfoCircleOutlined
        style={{ color: "#1890ff", cursor: "help", fontSize: 14 }}
        aria-label={`${text} help`}
      />
    </Tooltip>
  </span>
);

const uriRules = (label: string) => [
  { required: true, message: `${label} is required` },
  {
    validator: (_: unknown, value?: string) => {
      if (!value) return Promise.resolve();
      return S3_URI.test(value)
        ? Promise.resolve()
        : Promise.reject(new Error("Enter an s3:// URI with a bucket name"));
    },
  },
];

interface ScheduleExpressionInputProps {
  value?: string;
  onChange?: (val: string) => void;
}

/**
 * AWS EventBridge cron only (six fields, UTC). Rate expressions are not accepted.
 */
const ScheduleExpressionInput: React.FC<ScheduleExpressionInputProps> = ({
  value,
  onChange,
}) => {
  const inner = (value ?? "").replace(/^cron\(/, "").replace(/\)$/, "");

  return (
    <div>
      <Input
        value={inner}
        onChange={event => {
          const raw = event.target.value;
          onChange?.(raw.trim() ? `cron(${raw})` : "");
        }}
        placeholder="0 2 * * ? *"
        data-testid="cron-input"
      />
      <Text
        type="secondary"
        style={{ fontSize: 11, display: "block", marginTop: 4 }}
      >
        AWS EventBridge cron, six fields, UTC: minute hour day-of-month month
        day-of-week year
      </Text>
      {value ? (
        <Text
          type="secondary"
          style={{ fontSize: 12, display: "block", marginTop: 6 }}
        >
          Expression:{" "}
          <Text code style={{ fontSize: 12 }}>
            {value}
          </Text>
        </Text>
      ) : null}
    </div>
  );
};

const describeCreatedTransfer = (
  result: CreateTransferResult,
  sourceSpace: string,
  destinationSpace: string,
): string => {
  if ("schedule_name" in result && result.schedule_name) {
    const when = result.schedule_expression
      ? ` (${result.schedule_expression})`
      : "";
    return `Schedule ${result.schedule_name}${when} created for ${sourceSpace} → ${destinationSpace}.`;
  }
  if ("execution_id" in result && result.execution_id) {
    const status = result.status || "LAUNCHING";
    return `Adhoc transfer ${result.execution_id} is ${status} (${sourceSpace} → ${destinationSpace}).`;
  }
  return `Transfer from ${sourceSpace} → ${destinationSpace} submitted.`;
};

interface NewTransferTabProps {
  /** Display name of the currently opened space — locked as destination. */
  openedSpace: string;
  /** Canonical space identifier (space_name) — sent to the API; falls back to openedSpace. */
  openedSpaceApiName?: string | null;
  /** S3 bucket id for the opened space — used to pre-fill the destination URI. */
  openedSpaceBucket?: string | null;
  /** Region of the opened space — source dropdown is filtered to this region. */
  region?: string | null;
  /** Domain of the opened space — drives which spaces are selectable as source. */
  domainName?: string | null;
  msalInstance: IMsalContext;
  /** Called after a transfer is created — parent switches to Active tab. */
  onTransferSuccess?: (message: string) => void;
}

const NewTransferTab: React.FC<NewTransferTabProps> = ({
  openedSpace,
  openedSpaceApiName,
  openedSpaceBucket,
  region,
  domainName,
  msalInstance,
  onTransferSuccess,
}) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [mode, setMode] = useState<TransferMode>("Adhoc");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const isExternalDataExchange = domainName === EXTERNAL_DATA_EXCHANGE_DOMAIN;
  const openedUri = toS3Uri(openedSpaceBucket);
  const destinationSpaceName = openedSpaceApiName?.trim() || openedSpace;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const spaceDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [userSearch, setUserSearch] = useState<string>("");
  const userDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: selectableSpaces = [], isFetching: loadingSpaces } = useQuery({
    queryKey: [
      "dataTransfer",
      "transferSpaces",
      isExternalDataExchange ? "all-domains" : EXTERNAL_DATA_EXCHANGE_DOMAIN,
      region ?? "all",
      searchTerm,
    ],
    queryFn: () =>
      isExternalDataExchange
        ? fetchTransferSpaces(msalInstance, { searchTerm, region })
        : fetchSourceSpaces(msalInstance, searchTerm, region),
    staleTime: 60 * 1000,
  });

  const spacesForSelect = selectableSpaces.filter(
    (space: SourceSpace) => space.name !== destinationSpaceName,
  );

  useEffect(() => {
    if (openedUri) {
      form.setFieldsValue({ destination_uri: openedUri });
    }
  }, [openedUri, form]);

  const { data: userResults = [], isFetching: loadingUsers } = useQuery({
    queryKey: ["dataTransfer", "users", userSearch],
    queryFn: () => fetchUsers(msalInstance, userSearch),
    staleTime: 60 * 1000,
    enabled: userSearch.length >= 2,
  });

  const handleSpaceSearch = useCallback((value: string) => {
    if (spaceDebounce.current) clearTimeout(spaceDebounce.current);
    spaceDebounce.current = setTimeout(() => setSearchTerm(value), DEBOUNCE_MS);
  }, []);

  const handleUserSearch = useCallback((value: string) => {
    if (userDebounce.current) clearTimeout(userDebounce.current);
    userDebounce.current = setTimeout(() => setUserSearch(value), DEBOUNCE_MS);
  }, []);

  const handleSourceSpaceChange = (value: string | undefined) => {
    if (!value) {
      form.setFieldsValue({ source_uri: undefined });
      return;
    }
    const selected = spacesForSelect.find(
      (space: SourceSpace) => space.name === value,
    );
    const uri = toS3Uri(selected?.bucket_name);
    if (uri) form.setFieldsValue({ source_uri: uri });
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async values => {
        setSubmitting(true);
        setSubmitError("");

        const sourceSpace = values.source_space as string;
        const destinationSpace = destinationSpaceName;

        const payload: CreateTransferPayload = {
          source_space: sourceSpace,
          destination_space: destinationSpace,
          source_uri: values.source_uri,
          destination_uri: values.destination_uri,
          mode,
        };

        if (mode === "Scheduled") {
          payload.schedule_expression = values.schedule_expression;
          payload.end_date = values.end_date
            ? (values.end_date as Moment).format("YYYY-MM-DD")
            : undefined;
        }

        if (values.recipients?.length) {
          payload.recipients = values.recipients as string[];
        }

        try {
          const created = await createTransfer(msalInstance, payload);
          const successText = describeCreatedTransfer(
            created,
            sourceSpace,
            destinationSpace,
          );
          if (onTransferSuccess) {
            onTransferSuccess(successText);
          } else {
            message.success(successText);
          }
          form.resetFields();
          if (openedUri) {
            form.setFieldsValue({ destination_uri: openedUri });
          }
          setMode("Adhoc");
          setSearchTerm("");
          setUserSearch("");
          queryClient.invalidateQueries({
            queryKey: ["dataTransfer", "active", destinationSpace],
          });
        } catch (error: unknown) {
          const errMsg = extractDataTransferError(
            error,
            "Failed to create transfer. Please try again.",
          );
          setSubmitError(errMsg);
          message.error(errMsg);
        } finally {
          setSubmitting(false);
        }
      })
      .catch(() => {
        /* validation errors shown inline */
      });
  };

  const spaceOptionLabel = (space: SourceSpace) =>
    isExternalDataExchange && space.domain_name
      ? `${space.domain_name} — ${space.display_name}`
      : space.display_name;

  return (
    <div className="dt-tab-body">
      {submitError && (
        <Alert
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          message={submitError}
          closable
          onClose={() => setSubmitError("")}
        />
      )}

      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="source_space"
              label={<Text strong>Source Space</Text>}
              rules={[
                { required: true, message: "Please select a source space" },
              ]}
            >
              <Select
                showSearch
                placeholder="Type to search spaces…"
                allowClear
                filterOption={false}
                onSearch={handleSpaceSearch}
                onChange={handleSourceSpaceChange}
                notFoundContent={
                  loadingSpaces ? <Spin size="small" /> : "No spaces found"
                }
                loading={loadingSpaces}
                optionLabelProp="label"
              >
                {spacesForSelect.map((space: SourceSpace) => (
                  <Option
                    key={space.name}
                    value={space.name}
                    label={spaceOptionLabel(space)}
                  >
                    <div>{spaceOptionLabel(space)}</div>
                    {space.bucket_name && space.bucket_name !== space.name && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        s3://{space.bucket_name}/
                      </div>
                    )}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label={<Text strong>Destination Space</Text>}>
              <Input value={openedSpace} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="source_uri"
              label={<UriLabel text="Source URI" />}
              normalize={(value: string) =>
                value && !value.endsWith("/") ? `${value}/` : value
              }
              rules={uriRules("Source URI")}
            >
              <Input placeholder="s3://bucket-name/path/" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="destination_uri"
              label={<UriLabel text="Destination URI" />}
              initialValue={openedUri || undefined}
              normalize={(value: string) =>
                value && !value.endsWith("/") ? `${value}/` : value
              }
              rules={uriRules("Destination URI")}
            >
              <Input placeholder="s3://bucket-name/path/" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={<Text strong>Mode</Text>} style={{ marginBottom: 8 }}>
          <Space size={8}>
            <Tag.CheckableTag
              checked={mode === "Adhoc"}
              onChange={() => setMode("Adhoc")}
              className="dt-mode-tag"
            >
              Adhoc
            </Tag.CheckableTag>
            <Tag.CheckableTag
              checked={mode === "Scheduled"}
              onChange={() => setMode("Scheduled")}
              className="dt-mode-tag"
            >
              Scheduled
            </Tag.CheckableTag>
          </Space>
        </Form.Item>

        {mode === "Adhoc" ? (
          <Text
            type="secondary"
            style={{ display: "block", marginBottom: 16, fontSize: 13 }}
          >
            Starts immediately. The run shows on Active as LAUNCHING, then
            RUNNING.
          </Text>
        ) : (
          <Row gutter={16} style={{ marginBottom: 8 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="schedule_expression"
                label={<Text strong>Schedule</Text>}
                rules={[
                  {
                    required: true,
                    message: "Schedule expression is required",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      return AWS_CRON_EXPRESSION.test(value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Use a 6-field AWS cron, for example cron(0 2 * * ? *)",
                            ),
                          );
                    },
                  },
                ]}
              >
                <ScheduleExpressionInput />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="end_date"
                label={<Text strong>End Date</Text>}
                rules={[{ required: true, message: "End date is required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabledDate={(current: Moment) =>
                    !current || !current.isAfter(moment(), "day")
                  }
                  placeholder="Select end date"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Form.Item
          name="recipients"
          label={<Text strong>Recipients (extra email notifications)</Text>}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder="Type a name or email to search…"
            filterOption={false}
            onSearch={handleUserSearch}
            notFoundContent={
              loadingUsers ? (
                <Spin size="small" />
              ) : userSearch.length < 2 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Type at least 2 characters
                </Text>
              ) : (
                "No users found"
              )
            }
            loading={loadingUsers}
            optionLabelProp="label"
          >
            {userResults.map((user: IUser) => (
              <Option key={user.mail} value={user.mail} label={user.mail}>
                <div style={{ lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 500 }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {user.mail}
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              Start transfer
            </Button>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Starts a Glue run now, or creates an EventBridge schedule.
            </Text>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default NewTransferTab;
